import type { ReactNode } from 'react'
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'

import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type ConditionProposal = Extract<InFlightAgentActionProposal, { kind: 'condition_vocabulary' }>
type IndicatorProposal = Extract<InFlightAgentActionProposal, { kind: 'indicates' }>

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span> {children}
    </div>
  )
}

function settledSummary(
  proposal: ConditionProposal | IndicatorProposal,
  applied: string,
  rejected: string
) {
  if (proposal.terminalState?.kind === 'applied') {
    return <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">{applied}</p>
  }
  if (proposal.terminalState?.kind === 'apply_failed') {
    return (
      <p className="px-1 text-10 text-destructive">Apply failed — {proposal.terminalState.error}</p>
    )
  }
  if (proposal.terminalState?.kind === 'rejected') {
    return <p className="px-1 text-10 text-muted-foreground">{rejected}</p>
  }
  return null
}

export function indicatorRangeLabel(
  rangeLow: number | undefined,
  rangeHigh: number | undefined
): string | null {
  if (rangeLow === undefined && rangeHigh === undefined) return null
  return `${rangeLow ?? '−∞'} to ${rangeHigh ?? '∞'}`
}

export const conditionVocabularyViewCapability: AgentActionViewCapability = {
  kind: 'condition_vocabulary',
  badgeLabel() {
    return 'condition'
  },
  renderDetail(proposal) {
    if (proposal.kind !== 'condition_vocabulary') return null
    return (
      <div className="grid gap-1.5 rounded-xl border border-line/70 bg-muted/30 p-2 text-11">
        <Field label="Condition">
          <span className="font-medium">{proposal.name}</span>
        </Field>
        <Field label="Canonical ID">
          <span className="font-mono text-10">{proposal.conditionId}</span>
        </Field>
        <Field label="Kind">{proposal.conditionKind}</Field>
        <Field label="Perceivable by">{proposal.perceivableBy.join(', ') || 'not specified'}</Field>
        <Field label="Applies to">{proposal.equipmentType || 'not specified'}</Field>
        <Field label="How to check">{proposal.howToCheck || 'not specified'}</Field>
        <p className="text-muted-foreground">
          Apply updates the shared condition vocabulary and activates this inspection check.
        </p>
      </div>
    )
  },
  renderSettledSummary(proposal) {
    if (proposal.kind !== 'condition_vocabulary') return null
    return settledSummary(
      proposal,
      'Applied — condition and inspection applicability updated.',
      'Rejected — condition vocabulary left unchanged.'
    )
  }
}

export const indicatesViewCapability: AgentActionViewCapability = {
  kind: 'indicates',
  badgeLabel() {
    return 'indicator'
  },
  renderDetail(proposal) {
    if (proposal.kind !== 'indicates') return null
    const range = indicatorRangeLabel(proposal.rangeLow, proposal.rangeHigh)
    return (
      <div className="grid gap-1.5 rounded-xl border border-line/70 bg-muted/30 p-2 text-11">
        <Field label="Instrument">
          <span className="font-mono text-10">{proposal.instrumentId}</span>
        </Field>
        <Field label="Observable">
          <span className="font-mono text-10">{proposal.observableId}</span>
        </Field>
        {proposal.display ? <Field label="Display">{proposal.display}</Field> : null}
        {range ? <Field label="Range">{range}</Field> : null}
        {proposal.source ? <Field label="Source">{proposal.source}</Field> : null}
        <p className="text-muted-foreground">
          Apply links this existing local instrument to the observable it displays.
        </p>
      </div>
    )
  },
  renderSettledSummary(proposal) {
    if (proposal.kind !== 'indicates') return null
    return settledSummary(
      proposal,
      'Applied — indicator mapping updated.',
      'Rejected — indicator mapping left unchanged.'
    )
  }
}

registerAgentActionView(conditionVocabularyViewCapability)
registerAgentActionView(indicatesViewCapability)
